import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../lib/theme';

const s = {
  page: { minHeight: '100vh', background: C.cream, fontFamily: 'sans-serif', padding: '1.5rem 1rem' },
  wrap: { maxWidth: 480, margin: '0 auto', paddingBottom: '3rem' },
  card: { background: '#fff', borderRadius: 16, padding: '1.5rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' },
  label: { display: 'block', color: C.carbon, fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem', marginTop: '1rem' },
  input: { width: '100%', padding: '0.7rem 1rem', borderRadius: 8, border: `1.5px solid ${C.mint}`, fontSize: '0.95rem', boxSizing: 'border-box', background: C.cream, outline: 'none' },
  btn: { width: '100%', padding: '0.9rem', borderRadius: 8, border: 'none', background: C.sage, color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', marginTop: '1.2rem' },
  chip: (active) => ({
    padding: '0.45rem 0.9rem', borderRadius: 20, cursor: 'pointer', fontSize: '0.85rem',
    background: active ? C.sage : '#F8F4EF',
    color: active ? '#fff' : C.carbon,
    border: `1.5px solid ${active ? C.sage : C.mint}`,
    transition: 'all 0.15s',
  }),
};

export default function ClinicSignup({ onBack }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', day: '', slot: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const DAYS = [
    t('clinic_signup.day_mon'),
    t('clinic_signup.day_tue'),
    t('clinic_signup.day_wed'),
    t('clinic_signup.day_thu'),
    t('clinic_signup.day_fri'),
  ];

  const SLOTS = [
    t('clinic_signup.slot_morning'),
    t('clinic_signup.slot_afternoon'),
    t('clinic_signup.slot_any'),
  ];

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.name && form.email && form.phone && form.day && form.slot;

  async function handleSubmit() {
    if (!valid) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/clinic/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.ok) setSent(true);
      else setError(t('clinic_signup.error_send'));
    } catch {
      setError(t('clinic_signup.error_connection'));
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div style={s.page}>
        <div style={{ ...s.wrap, textAlign: 'center', paddingTop: '3rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌿</div>
          <h2 style={{ fontFamily: 'Georgia, serif', color: C.sage, fontSize: '1.4rem' }}>{t('clinic_signup.success_title')}</h2>
          <p style={{ color: C.carbon, fontSize: '0.95rem', lineHeight: 1.6, margin: '0.5rem 0 1.5rem' }}>
            {t('clinic_signup.success_text')}
          </p>
          <button onClick={() => window.location.href = '/'} style={{ ...s.btn, maxWidth: 280, margin: '0 auto' }}>
            {t('clinic_signup.success_btn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.wrap}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem' }}>🌿</div>
          <h1 style={{ fontFamily: 'Georgia, serif', color: C.sage, fontSize: '1.4rem', margin: '0.3rem 0 0.2rem' }}>
            {t('clinic_signup.title')}
          </h1>
          <p style={{ color: C.gray, fontSize: '0.88rem', margin: 0 }}>
            {t('clinic_signup.subtitle')}
          </p>
        </div>

        <div style={s.card}>
          <label style={s.label}>{t('clinic_signup.clinic_name_label')}</label>
          <input style={s.input} value={form.name} onChange={e => update('name', e.target.value)} placeholder={t('clinic_signup.clinic_name_placeholder')} />

          <label style={s.label}>{t('clinic_signup.email_label')}</label>
          <input style={s.input} type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder={t('clinic_signup.email_placeholder')} />

          <label style={s.label}>{t('clinic_signup.phone_label')}</label>
          <input style={s.input} type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder={t('clinic_signup.phone_placeholder')} />

          <label style={s.label}>{t('clinic_signup.day_label')}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem' }}>
            {DAYS.map(d => (
              <button key={d} onClick={() => update('day', d)} style={s.chip(form.day === d)}>{d}</button>
            ))}
          </div>

          <label style={s.label}>{t('clinic_signup.slot_label')}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem' }}>
            {SLOTS.map(sl => (
              <button key={sl} onClick={() => update('slot', sl)} style={s.chip(form.slot === sl)}>{sl}</button>
            ))}
          </div>

          {error && <p style={{ color: C.terra, fontSize: '0.85rem', marginTop: '0.8rem' }}>{error}</p>}

          <button style={{ ...s.btn, opacity: valid && !loading ? 1 : 0.5 }} disabled={!valid || loading} onClick={handleSubmit}>
            {loading ? t('clinic_signup.submitting') : t('clinic_signup.submit_btn')}
          </button>

          <button onClick={onBack} style={{ ...s.btn, background: 'none', color: C.gray, border: `1px solid #E5E7EB`, marginTop: '0.5rem' }}>
            {t('clinic_signup.back_btn')}
          </button>
        </div>

        <p style={{ fontSize: '0.78rem', color: C.gray, textAlign: 'center' }}>
          {t('clinic_signup.disclaimer')}
        </p>
      </div>
    </div>
  );
}
